import { useTranslation } from 'react-i18next';
import { Controller, useForm } from 'react-hook-form';
import { joiResolver } from '@hookform/resolvers/joi';
import { createJoiWithTranslations } from '../../utils/joiConfig';
import { Input, Select, FormRow } from '@efast_public/fjd-component-library';
import { Zustellwege } from '../../api/govos.types';
import {
  FitConnectFormProps,
  ZustellwegSubFormProps
} from './Zustellweg.types';
import { useZustellwegForm } from './useZustellwegForm';
import { TFunction } from 'i18next';

const getDefaultValues = (data?: Zustellwege): FitConnectFormProps => ({
  serviceId: data?.serviceId || 0,
  destinationid: data?.params?.destinationid || ''
});

const getValidationSchema = (
  CustomJoi: ReturnType<typeof createJoiWithTranslations>,
  t: TFunction<'translation', undefined>
) =>
  CustomJoi.object<FitConnectFormProps>({
    serviceId: CustomJoi.string().required(),
    destinationid: CustomJoi.string()
      .required()
      .pattern(
        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/
      )
      .message(t('FitConnectForm.validation.destinationIdInvalidFormat'))
  });

export function FitConnectForm({
  deliveryServices,
  zustellwege,
  serviceIndex,
  ...props
}: ZustellwegSubFormProps) {
  const filteredDeliveryServices =
    deliveryServices?.filter(
      (s) => s.serviceCode === 'fitcondeliveryservice'
    ) ?? [];

  const { t } = useTranslation();
  const CustomJoi = createJoiWithTranslations(t);

  const { register, formState, control, handleSubmit } =
    useForm<FitConnectFormProps>({
      defaultValues: getDefaultValues(zustellwege?.at(serviceIndex)),
      resolver: joiResolver(getValidationSchema(CustomJoi, t)),
      mode: 'onBlur'
    });

  useZustellwegForm({
    deliveryServices,
    zustellwege,
    serviceIndex,
    handleSubmit,
    ...props
  });

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
      <FormRow label={t('EditTransportConfigModal.fields.fitConnectService')}>
        <Controller
          name="serviceId"
          control={control}
          render={({ field }) => (
            <Select
              value={field.value}
              onChange={(value) => {
                const selectedValue = value.at(0)?.value;
                field.onChange(selectedValue);
              }}
              onBlur={() => field.onBlur()}
              name={field.name}
              options={
                filteredDeliveryServices?.map((s) => ({
                  value: String(s.serviceId),
                  label: s.title
                })) ?? []
              }
              defaultOptions={
                serviceIndex > -1
                  ? (filteredDeliveryServices
                      ?.filter(
                        (s) =>
                          s.serviceId ===
                          zustellwege?.at(serviceIndex)?.serviceId
                      )
                      .map((s) => ({
                        value: String(s.serviceId),
                        label: s.title
                      })) ?? [])
                  : []
              }
              placeholder={
                filteredDeliveryServices?.length === 0
                  ? t('select.noOptionsAvailable')
                  : t('EditTransportConfigModal.fields.selectFitConnectService')
              }
              disabled={filteredDeliveryServices?.length === 0}
            />
          )}
        />
      </FormRow>

      <FormRow
        label={t('FitConnectForm.fields.destinationId')}
        tooltip={t('FitConnectForm.fields.destinationIdHint')}
        validationMessage={
          formState.errors.destinationid?.message
            ? t(formState.errors.destinationid.message)
            : undefined
        }
        variant={formState.errors.destinationid ? 'error' : undefined}
      >
        <Input
          {...register('destinationid')}
          placeholder={t('FitConnectForm.fields.destinationIdPlaceholder')}
        />
      </FormRow>
    </div>
  );
}
